import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const SessionReportsList = ({ data, filters = {}, isLoading = false }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const attendance = data?.attendance || [];
  const topicSummary = data?.topicSummary || [];
  const periodSummary = data?.periodSummary || {};
  const antiStigmaProgram = data?.antiStigmaProgram || {};
  const comparisons = data?.comparisons || {};

  const totalRows = attendance.length;
  const start = totalRows > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const attendanceColumns = [
    {
      name: "S.No",
      cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "80px",
    },
    {
      name: "Patient MR No.",
      selector: (row) => row.mr_no || "—",
      sortable: true,
      width: "180px",
    },
    {
      name: "Client/Patient Name",
      selector: (row) => row.patient_name || "—",
      sortable: true,
      cell: (row) => <span className="fw-semibold text-dark">{row.patient_name}</span>,
    },
    {
      name: "Sessions Attended",
      selector: (row) => row.sessions_attended || 0,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <span className="badge bg-label-primary fs-7">
          {row.sessions_attended} sessions
        </span>
      ),
    },
  ];

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Session Reporting & Statistics Report", 14, 15);

    // Period counts
    doc.setFontSize(12);
    doc.text("1. Period Summary (Sessions)", 14, 25);
    const summaryHeaders = ["Category", "Weekly", "Monthly", "Yearly"];
    const summaryRows = [
      ["PRP Sessions (Total)", periodSummary.prp?.weekly || 0, periodSummary.prp?.monthly || 0, periodSummary.prp?.yearly || 0],
      ["Core Sessions", periodSummary.core?.weekly || 0, periodSummary.core?.monthly || 0, periodSummary.core?.yearly || 0],
      ["Nursing Sessions", periodSummary.nursing?.weekly || 0, periodSummary.nursing?.monthly || 0, periodSummary.nursing?.yearly || 0],
      ["Anti-Stigma Sessions", periodSummary.antiStigma?.weekly || 0, periodSummary.antiStigma?.monthly || 0, periodSummary.antiStigma?.yearly || 0],
      ["IMR Sessions (Evidence-Based)", periodSummary.imr?.weekly || 0, periodSummary.imr?.monthly || 0, periodSummary.imr?.yearly || 0],
      ["Family Psychoeducation Sessions (Evidence-Based)", periodSummary.familyPsychoeducation?.weekly || 0, periodSummary.familyPsychoeducation?.monthly || 0, periodSummary.familyPsychoeducation?.yearly || 0],
      ["Total Evidence-Based Sessions", periodSummary.totalEvidenceBased?.weekly || 0, periodSummary.totalEvidenceBased?.monthly || 0, periodSummary.totalEvidenceBased?.yearly || 0],
      ["Total Conducted Sessions", periodSummary.total?.weekly || 0, periodSummary.total?.monthly || 0, periodSummary.total?.yearly || 0],
    ];
    autoTable(doc, {
      head: [summaryHeaders],
      body: summaryRows,
      startY: 28,
    });

    // Anti-Stigma metrics
    doc.text("2. Anti-Stigma Program Specifics", 14, doc.lastAutoTable.finalY + 12);
    const antiStigmaHeaders = ["Metric Indicator", "Value Details"];
    const antiStigmaRows = [
      ["Clients Enrolled", antiStigmaProgram.clientsEnrolled || 0],
      ["Clients Attended > 16 Sessions (Mandatory)", antiStigmaProgram.clientsWithOver16Sessions || 0],
      ["Weekly Conducted Sessions", antiStigmaProgram.weeklyCount || 0],
      ["Monthly Conducted Sessions", antiStigmaProgram.monthlyCount || 0],
      ["Yearly Conducted Sessions", antiStigmaProgram.yearlyCount || 0],
      ["Yearly Growth / Trend Comparison", `${antiStigmaProgram.yearlyComparison?.current || 0} vs ${antiStigmaProgram.yearlyComparison?.previous || 0} (${antiStigmaProgram.yearlyComparison?.change || "0%"})`],
    ];
    autoTable(doc, {
      head: [antiStigmaHeaders],
      body: antiStigmaRows,
      startY: doc.lastAutoTable.finalY + 15,
    });

    // Comparisons
    doc.text("3. Period-over-Period Trend Comparisons", 14, doc.lastAutoTable.finalY + 12);
    const compareHeaders = ["Comparison Period", "Current Count", "Previous Count", "Change (%)"];
    const compareRows = [
      ["Weekly (Current vs Last Week)", comparisons.weekly?.current || 0, comparisons.weekly?.previous || 0, comparisons.weekly?.change || "0%"],
      ["Monthly (Current vs Last Month)", comparisons.monthly?.current || 0, comparisons.monthly?.previous || 0, comparisons.monthly?.change || "0%"],
      ["Yearly (Current vs Last Year)", comparisons.yearly?.current || 0, comparisons.yearly?.previous || 0, comparisons.yearly?.change || "0%"],
    ];
    autoTable(doc, {
      head: [compareHeaders],
      body: compareRows,
      startY: doc.lastAutoTable.finalY + 15,
    });

    // Client Attendance List
    doc.addPage();
    doc.text("4. Client Attendance Tracking List", 14, 15);
    const attendanceHeaders = ["S.No", "MR Number", "Patient Name", "Sessions Attended"];
    const attendanceRows = attendance.map((item, index) => [
      index + 1,
      item.mr_no || "—",
      item.patient_name || "—",
      item.sessions_attended || 0,
    ]);
    autoTable(doc, {
      head: [attendanceHeaders],
      body: attendanceRows,
      startY: 20,
    });

    doc.save("session_reporting_summary.pdf");
  };

  const getCSVData = () => {
    const dataRows = [];
    dataRows.push(["Session Reporting & Statistics Report"]);
    dataRows.push([]);

    // Period counts
    dataRows.push(["Period Counts Summary"]);
    dataRows.push(["Category", "Weekly", "Monthly", "Yearly"]);
    dataRows.push(["PRP Sessions (Total)", periodSummary.prp?.weekly || 0, periodSummary.prp?.monthly || 0, periodSummary.prp?.yearly || 0]);
    dataRows.push(["Core Sessions", periodSummary.core?.weekly || 0, periodSummary.core?.monthly || 0, periodSummary.core?.yearly || 0]);
    dataRows.push(["Nursing Sessions", periodSummary.nursing?.weekly || 0, periodSummary.nursing?.monthly || 0, periodSummary.nursing?.yearly || 0]);
    dataRows.push(["Anti-Stigma Sessions", periodSummary.antiStigma?.weekly || 0, periodSummary.antiStigma?.monthly || 0, periodSummary.antiStigma?.yearly || 0]);
    dataRows.push(["IMR Sessions (Evidence-Based)", periodSummary.imr?.weekly || 0, periodSummary.imr?.monthly || 0, periodSummary.imr?.yearly || 0]);
    dataRows.push(["Family Psychoeducation Sessions (Evidence-Based)", periodSummary.familyPsychoeducation?.weekly || 0, periodSummary.familyPsychoeducation?.monthly || 0, periodSummary.familyPsychoeducation?.yearly || 0]);
    dataRows.push(["Total Evidence-Based Sessions", periodSummary.totalEvidenceBased?.weekly || 0, periodSummary.totalEvidenceBased?.monthly || 0, periodSummary.totalEvidenceBased?.yearly || 0]);
    dataRows.push(["Total Conducted Sessions", periodSummary.total?.weekly || 0, periodSummary.total?.monthly || 0, periodSummary.total?.yearly || 0]);
    dataRows.push([]);

    // Anti-Stigma
    dataRows.push(["Anti-Stigma Program Specifics"]);
    dataRows.push(["Clients Enrolled", antiStigmaProgram.clientsEnrolled || 0]);
    dataRows.push(["Clients Attended > 16 Sessions (Mandatory)", antiStigmaProgram.clientsWithOver16Sessions || 0]);
    dataRows.push(["Weekly Conducted Sessions", antiStigmaProgram.weeklyCount || 0]);
    dataRows.push(["Monthly Conducted Sessions", antiStigmaProgram.monthlyCount || 0]);
    dataRows.push(["Yearly Conducted Sessions", antiStigmaProgram.yearlyCount || 0]);
    dataRows.push(["Yearly Growth", `${antiStigmaProgram.yearlyComparison?.current || 0} vs ${antiStigmaProgram.yearlyComparison?.previous || 0} (${antiStigmaProgram.yearlyComparison?.change || "0%"})`]);
    dataRows.push([]);

    // Comparisons
    dataRows.push(["Period-over-Period Trend Comparisons"]);
    dataRows.push(["Comparison Period", "Current Count", "Previous Count", "Change (%)"]);
    dataRows.push(["Weekly", comparisons.weekly?.current || 0, comparisons.weekly?.previous || 0, comparisons.weekly?.change || "0%"]);
    dataRows.push(["Monthly", comparisons.monthly?.current || 0, comparisons.monthly?.previous || 0, comparisons.monthly?.change || "0%"]);
    dataRows.push(["Yearly", comparisons.yearly?.current || 0, comparisons.yearly?.previous || 0, comparisons.yearly?.change || "0%"]);
    dataRows.push([]);

    // Attendance
    dataRows.push(["Client Attendance Tracking"]);
    dataRows.push(["S.No", "MR Number", "Patient Name", "Sessions Attended"]);
    attendance.forEach((item, index) => {
      dataRows.push([
        index + 1,
        item.mr_no || "—",
        item.patient_name || "—",
        item.sessions_attended || 0,
      ]);
    });

    return dataRows;
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
            {[5, 10, 15, 20, 25].map((option) => (<option key={option} value={option}>{option}</option>))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li><CSVLink data={getCSVData()} filename="session_reports.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
            </ul>
          </div>
        </div>
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} clients
        </h6>
      </div>

      <div className="card-body p-4">
        {/* Core summary cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card text-center p-3 border bg-light-subtle h-100 shadow-sm">
              <label className="text-secondary small fw-bold mb-1">Total Conducted Sessions</label>
              <h3 className="text-primary fw-bold mb-0">{periodSummary.total?.yearly || 0}</h3>
              <span className="text-muted small">This Year</span>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center p-3 border bg-light-subtle h-100 shadow-sm">
              <label className="text-secondary small fw-bold mb-1">Weekly Conducted Sessions</label>
              <h3 className="text-success fw-bold mb-0">{periodSummary.total?.weekly || 0}</h3>
              <span className="text-muted small">This Week</span>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center p-3 border bg-light-subtle h-100 shadow-sm">
              <label className="text-secondary small fw-bold mb-1">Monthly Conducted Sessions</label>
              <h3 className="text-info fw-bold mb-0">{periodSummary.total?.monthly || 0}</h3>
              <span className="text-muted small">This Month</span>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center p-3 border bg-light-subtle h-100 shadow-sm">
              <label className="text-secondary small fw-bold mb-1">Clients Tracked (Active)</label>
              <h3 className="text-dark fw-bold mb-0">{totalRows}</h3>
              <span className="text-muted small">Total Attended</span>
            </div>
          </div>
        </div>

        {/* Breakdown & Anti-Stigma stats */}
        <div className="row g-4 mb-4">
          {/* Periodic summary breakdown */}
          <div className="col-md-6">
            <div className="card border h-100 shadow-sm bg-white">
              <div className="card-header bg-transparent border-bottom p-3">
                <h5 className="mb-0 text-dark"><i className="ri-list-check me-2 text-primary"></i>Session Topic Breakdown</h5>
              </div>
              <div className="card-body p-3">
                <div className="table-responsive">
                  <table className="table table-bordered table-striped mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Topic Name</th>
                        <th className="text-center" style={{ width: "130px" }}>Total Sessions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topicSummary.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center text-muted">No topic data available</td>
                        </tr>
                      ) : (
                        topicSummary.map((topic, i) => (
                          <tr key={i}>
                            <td className="fw-semibold text-dark">{topic.topic}</td>
                            <td className="text-center font-monospace">{topic.sessions_count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Anti-Stigma Specifics */}
          <div className="col-md-6">
            <div className="card border h-100 shadow-sm bg-white">
              <div className="card-header bg-transparent border-bottom p-3">
                <h5 className="mb-0 text-dark"><i className="ri-focus-3-line me-2 text-danger"></i>Anti-Stigma Program Metrics</h5>
              </div>
              <div className="card-body p-3">
                <div className="row g-3">
                  <div className="col-6">
                    <div className="p-3 border rounded text-center bg-light shadow-sm">
                      <h6 className="text-secondary mb-1">Clients Enrolled</h6>
                      <h4 className="fw-bold mb-0 text-dark">{antiStigmaProgram.clientsEnrolled || 0}</h4>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 border rounded text-center bg-light shadow-sm">
                      <h6 className="text-secondary mb-1">&gt; 16 Sessions (Mandatory)</h6>
                      <h4 className="fw-bold mb-0 text-success">{antiStigmaProgram.clientsWithOver16Sessions || 0}</h4>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 border rounded text-center shadow-sm">
                      <h6 className="text-secondary small mb-1">Weekly</h6>
                      <h5 className="fw-bold mb-0">{antiStigmaProgram.weeklyCount || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 border rounded text-center shadow-sm">
                      <h6 className="text-secondary small mb-1">Monthly</h6>
                      <h5 className="fw-bold mb-0">{antiStigmaProgram.monthlyCount || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 border rounded text-center shadow-sm">
                      <h6 className="text-secondary small mb-1">Yearly</h6>
                      <h5 className="fw-bold mb-0">{antiStigmaProgram.yearlyCount || 0}</h5>
                    </div>
                  </div>
                  
                  <div className="col-12 mt-2">
                    <div className="d-flex justify-content-between align-items-center p-3 border rounded bg-light-subtle shadow-sm">
                      <span className="fw-semibold text-dark">Yearly Trend vs Last Year:</span>
                      <span className={`badge ${antiStigmaProgram.yearlyComparison?.change?.startsWith("-") ? "bg-label-danger" : "bg-label-success"} fs-6 fw-bold`}>
                        {antiStigmaProgram.yearlyComparison?.current || 0} vs {antiStigmaProgram.yearlyComparison?.previous || 0} ({antiStigmaProgram.yearlyComparison?.change || "0%"})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparisons & Attendance grid */}
        <div className="row g-4">
          {/* PRP vs Nursing count counts */}
          <div className="col-md-6">
            <div className="card border h-100 shadow-sm bg-white">
              <div className="card-header bg-transparent border-bottom p-3">
                <h5 className="mb-0 text-dark"><i className="ri-bar-chart-grouped-line me-2 text-info"></i>Weekly, Monthly, Yearly summaries</h5>
              </div>
              <div className="card-body p-3">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Category Type</th>
                        <th className="text-center">Weekly</th>
                        <th className="text-center">Monthly</th>
                        <th className="text-center">Yearly</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="fw-bold">PRP Sessions (Total)</td>
                        <td className="text-center font-monospace fw-semibold">{periodSummary.prp?.weekly || 0}</td>
                        <td className="text-center font-monospace fw-semibold">{periodSummary.prp?.monthly || 0}</td>
                        <td className="text-center font-monospace fw-semibold">{periodSummary.prp?.yearly || 0}</td>
                      </tr>
                      <tr>
                        <td className="fw-normal text-secondary ps-4">└ Core Sessions</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.core?.weekly || 0}</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.core?.monthly || 0}</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.core?.yearly || 0}</td>
                      </tr>
                      <tr>
                        <td className="fw-normal text-secondary ps-4">└ IMR Sessions (Evidence-Based)</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.imr?.weekly || 0}</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.imr?.monthly || 0}</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.imr?.yearly || 0}</td>
                      </tr>
                      <tr>
                        <td className="fw-normal text-secondary ps-4">└ Family Psychoeducation (Evidence-Based)</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.familyPsychoeducation?.weekly || 0}</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.familyPsychoeducation?.monthly || 0}</td>
                        <td className="text-center font-monospace text-secondary">{periodSummary.familyPsychoeducation?.yearly || 0}</td>
                      </tr>
                      <tr className="table-info">
                        <td className="fw-bold text-info-emphasis">Total Evidence-Based Sessions</td>
                        <td className="text-center font-monospace fw-bold text-info-emphasis">{periodSummary.totalEvidenceBased?.weekly || 0}</td>
                        <td className="text-center font-monospace fw-bold text-info-emphasis">{periodSummary.totalEvidenceBased?.monthly || 0}</td>
                        <td className="text-center font-monospace fw-bold text-info-emphasis">{periodSummary.totalEvidenceBased?.yearly || 0}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Nursing Sessions</td>
                        <td className="text-center font-monospace">{periodSummary.nursing?.weekly || 0}</td>
                        <td className="text-center font-monospace">{periodSummary.nursing?.monthly || 0}</td>
                        <td className="text-center font-monospace">{periodSummary.nursing?.yearly || 0}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Anti-Stigma Sessions</td>
                        <td className="text-center font-monospace">{periodSummary.antiStigma?.weekly || 0}</td>
                        <td className="text-center font-monospace">{periodSummary.antiStigma?.monthly || 0}</td>
                        <td className="text-center font-monospace">{periodSummary.antiStigma?.yearly || 0}</td>
                      </tr>
                      <tr className="table-secondary">
                        <td className="fw-bold text-dark">Total Conducted Sessions</td>
                        <td className="text-center font-monospace fw-bold text-dark">{periodSummary.total?.weekly || 0}</td>
                        <td className="text-center font-monospace fw-bold text-dark">{periodSummary.total?.monthly || 0}</td>
                        <td className="text-center font-monospace fw-bold text-dark">{periodSummary.total?.yearly || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-3">
                  <h6 className="text-dark fw-bold mb-2">Trend Comparisons (Total Conducted)</h6>
                  <div className="row g-2">
                    <div className="col-4">
                      <div className="p-2 border rounded text-center bg-light-subtle h-100 shadow-sm">
                        <span className="text-secondary small d-block fw-semibold">Weekly Trend</span>
                        <div className="fw-bold my-1 text-dark" style={{ fontSize: "0.85rem" }}>
                          {comparisons.weekly?.current ?? 0} <span className="text-muted fw-normal">vs</span> {comparisons.weekly?.previous ?? 0}
                          <span className="text-muted small d-block" style={{ fontSize: "0.72rem" }}>vs Last Week</span>
                        </div>
                        <span className={`badge ${comparisons.weekly?.diff < 0 || comparisons.weekly?.change?.startsWith("-") ? "bg-label-danger" : "bg-label-success"} fw-bold`}>
                          {comparisons.weekly?.change || "0%"}
                        </span>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="p-2 border rounded text-center bg-light-subtle h-100 shadow-sm">
                        <span className="text-secondary small d-block fw-semibold">Monthly Trend</span>
                        <div className="fw-bold my-1 text-dark" style={{ fontSize: "0.85rem" }}>
                          {comparisons.monthly?.current ?? 0} <span className="text-muted fw-normal">vs</span> {comparisons.monthly?.previous ?? 0}
                          <span className="text-muted small d-block" style={{ fontSize: "0.72rem" }}>vs Last Month</span>
                        </div>
                        <span className={`badge ${comparisons.monthly?.diff < 0 || comparisons.monthly?.change?.startsWith("-") ? "bg-label-danger" : "bg-label-success"} fw-bold`}>
                          {comparisons.monthly?.change || "0%"}
                        </span>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="p-2 border rounded text-center bg-light-subtle h-100 shadow-sm">
                        <span className="text-secondary small d-block fw-semibold">Yearly Trend</span>
                        <div className="fw-bold my-1 text-dark" style={{ fontSize: "0.85rem" }}>
                          {comparisons.yearly?.current ?? 0} <span className="text-muted fw-normal">vs</span> {comparisons.yearly?.previous ?? 0}
                          <span className="text-muted small d-block" style={{ fontSize: "0.72rem" }}>vs Last Year</span>
                        </div>
                        <span className={`badge ${comparisons.yearly?.diff < 0 || comparisons.yearly?.change?.startsWith("-") ? "bg-label-danger" : "bg-label-success"} fw-bold`}>
                          {comparisons.yearly?.change || "0%"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client Attendance tracking list */}
          <div className="col-md-6">
            <div className="card border h-100 shadow-sm bg-white">
              <div className="card-header bg-transparent border-bottom p-3">
                <h5 className="mb-0 text-dark"><i className="ri-team-line me-2 text-warning"></i>Client Attendance Tracking</h5>
              </div>
              <div className="card-body p-2">
                <DataTable
                  columns={attendanceColumns}
                  data={attendance}
                  pagination
                  paginationPerPage={rowsPerPage}
                  paginationTotalRows={totalRows}
                  onChangeRowsPerPage={setRowsPerPage}
                  onChangePage={setCurrentPage}
                  paginationComponent={CustomPagination}
                  highlightOnHover
                  striped
                  responsive
                  progressPending={isLoading}
                  noDataComponent={<div className="p-3 text-muted">No attendance records found</div>}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionReportsList;
